import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, Ban, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { RoleChip, TierChip } from "@/components/admin/roleMeta";

/**
 * Customers tab — filtered list of `chef` role only.
 * Tier column uses colour-coded badges (Individual = neutral, Pro = gold).
 */
export default function CustomersTab({ me, registerRefresh }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("");  // 'individual' | 'professional' | ''
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState(null);

  const isRoot = me?.role === "root";
  const canWrite = me?.role === "root" || me?.role === "staff_edit";

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${session?.access_token || ""}` };
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const h = await authHeaders();
      const params = new URLSearchParams({ page, page_size: pageSize, role: "chef" });
      if (q) params.set("q", q);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: h });
      let list = res.data.users || [];
      if (tierFilter) list = list.filter((u) => (u.subscription_tier || "individual") === tierFilter);
      setUsers(list);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, page, pageSize, q, tierFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => {
    if (registerRefresh) registerRefresh(() => fetchUsers);
  }, [registerRefresh, fetchUsers]);

  const suspendUser = async (userId) => {
    if (!window.confirm("Suspend this customer? Their JWTs will be rejected.")) return;
    setBusyUserId(userId);
    try {
      const h = await authHeaders();
      await axios.post(`${API}/admin/users/${userId}/suspend`, {}, { headers: h });
      toast.success("Customer suspended");
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suspend failed");
    } finally {
      setBusyUserId(null);
    }
  };

  const restoreUser = async (userId) => {
    setBusyUserId(userId);
    try {
      const h = await authHeaders();
      await axios.post(`${API}/admin/users/${userId}/restore`, {}, { headers: h });
      toast.success("Customer restored");
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="hq-tab-customers">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search email, name, restaurant…"
            className="pl-9 bg-slate-900 border-slate-700 text-white"
            data-testid="hq-customers-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { k: "", label: "All Tiers" },
            { k: "individual", label: "Individual" },
            { k: "professional", label: "Pro" },
          ].map((opt) => (
            <button
              key={opt.k || "all"}
              type="button"
              onClick={() => { setTierFilter(opt.k); setPage(1); }}
              className={cn(
                "px-3 py-1.5 rounded text-xs font-semibold border transition-colors",
                tierFilter === opt.k
                  ? "bg-amber-500 text-slate-950 border-amber-400"
                  : "bg-slate-900 text-slate-400 border-slate-700 hover:border-amber-500/50"
              )}
              data-testid={"hq-customers-tier-filter-" + (opt.k || "all")}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500 ml-1 font-mono">{users.length} of {total}</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 border-b border-slate-800">
            <tr className="text-left text-[0.65rem] uppercase tracking-[0.18em] text-slate-500">
              <th className="px-4 py-2.5 font-semibold">Customer</th>
              <th className="px-4 py-2.5 font-semibold">Tier</th>
              <th className="px-4 py-2.5 font-semibold">Restaurant</th>
              <th className="px-4 py-2.5 font-semibold">Region</th>
              <th className="px-4 py-2.5 font-semibold">Pro Expires</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="hq-customers-tbody">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No customers match the filter.</td></tr>
            ) : users.map((u) => (
              <tr
                key={u.user_id}
                className={cn(
                  "border-b border-slate-800 last:border-0 hover:bg-slate-900/80",
                  u.disabled_at && "opacity-40"
                )}
                data-testid={"hq-customers-row-" + u.user_id}
              >
                <td className="px-4 py-2.5">
                  <div className="text-white text-xs font-semibold">{u.email || u.full_name || "—"}</div>
                  <div className="text-[0.65rem] text-slate-500 font-mono">{u.user_id.slice(0, 8)}…</div>
                </td>
                <td className="px-4 py-2.5">
                  <TierChip tier={u.subscription_tier || "individual"} />
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{u.restaurant_name || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400">
                  {u.billing_region === "IN" ? "🇮🇳 IN" : u.billing_region === "RoW" ? "🌍 RoW" : "—"}
                </td>
                <td className="px-4 py-2.5 text-[0.7rem] font-mono text-slate-400">
                  {u.pro_expires_at ? new Date(u.pro_expires_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {u.disabled_at ? (
                    <span className="text-rose-400 inline-flex items-center gap-1"><Ban className="h-3 w-3" />Suspended</span>
                  ) : (
                    <span className="text-emerald-400 inline-flex items-center gap-1">Active</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    {isRoot && !u.disabled_at && (
                      <button
                        type="button"
                        onClick={() => suspendUser(u.user_id)}
                        disabled={busyUserId === u.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                        data-testid={"hq-customers-suspend-" + u.user_id}
                        title="Suspend customer"
                      ><Ban className="h-3 w-3 inline" /></button>
                    )}
                    {canWrite && u.disabled_at && (
                      <button
                        type="button"
                        onClick={() => restoreUser(u.user_id)}
                        disabled={busyUserId === u.user_id}
                        className="text-[0.65rem] px-2 py-1 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        data-testid={"hq-customers-restore-" + u.user_id}
                        title="Restore customer"
                      ><RotateCcw className="h-3 w-3 inline" /></button>
                    )}
                    {!canWrite && (
                      <RoleChip role="staff_view" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}
                className="bg-slate-900 border-slate-700 disabled:opacity-30" data-testid="hq-customers-prev">Prev</Button>
        <span className="font-mono">page {page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
        <Button variant="outline" size="sm" disabled={page * pageSize >= total || loading} onClick={() => setPage(page + 1)}
                className="bg-slate-900 border-slate-700 disabled:opacity-30" data-testid="hq-customers-next">Next</Button>
      </div>
    </div>
  );
}
